// eslint-disable-next-line
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ScenarioList from './components/Scenarios/ScenarioList';
import TestFlow from './components/Tests/TestFlow';
import AdminTools from './components/Admin/AdminTools';
import AdvancedScenarioList from './components/AdvancedScenarios/AdvancedScenarioList';
import AdvancedScenarioDetail from './components/AdvancedScenarios/AdvancedScenarioDetail';
import TestResult from './components/Tests/TestResult';
import HomePage from './components/Home/HomePage';
import SequentialTestFlow from './components/Tests/SequentialTestFlow';
import SurveyTransition from './components/SurveyTransition/SurveyTransition';
import PersonalQuestionnaire from './components/PersonalQuestionnaire/PersonalQuestionnaire';
import SurveyComplete from './components/SurveyTransition/SurveyComplete';
import CustomTest from './components/CustomTest/CustomTest';
import AnalysisReport from './components/AnalysisReport/AnalysisReport';
import ShareButton from './components/shared/ShareButton';
import './App.css';

// 简单的页面视图跟踪函数
const trackPageView = (path) => {
  console.log(`Page view: ${path}`);
};

// Scroll to top component that scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>An Error Occurred</h2>
          <p>Sorry, the application encountered a problem. Please refresh the page to try again.</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected route component
function ProtectedRoute({ children }) {
  const { currentUser } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return children;
}

function AppContent() {
  const { currentUser, isAdmin, logout } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    // Track page views
    trackPageView(location.pathname);
  }, [location]);
  
  // 删除处理Test导航项的点击函数
  
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };
  
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <header className="app-header">
        <div className="logo">
          <h1>Social Aptitude</h1>
        </div>
        
        <nav>
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/personal-questionnaire">Test</Link></li>
            <li><Link to="/advanced-scenarios">More Scenarios</Link></li>
            
            {currentUser ? (
              <>
                <li><button onClick={handleLogout}>Logout</button></li>
              </>
            ) : (
              <>
                <li><Link to="/login">Login</Link></li>
                <li><Link to="/register">Register</Link></li>
              </>
            )}
          </ul>
        </nav>
      </header>
      
      <main className="app-content">
        {isAdmin && <AdminTools />}
        
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/scenarios" element={<ScenarioList />} />
          <Route path="/test/:scenarioId" element={
            <ProtectedRoute>
              <TestFlow />
            </ProtectedRoute>
          } />
          <Route path="/full-test" element={<TestFlow />} />
          <Route path="/sequential-test" element={<SequentialTestFlow />} />
          <Route path="/test-result" element={<TestResult />} />
          <Route path="/advanced-scenarios" element={<AdvancedScenarioList />} />
          <Route path="/advanced-scenarios/:scenarioId" element={<AdvancedScenarioDetail />} />
          
          {/* 新增的路由 */}
          <Route path="/personal-questionnaire" element={<PersonalQuestionnaire />} />
          <Route path="/survey-transition" element={<SurveyTransition />} />
          <Route path="/survey-complete" element={<SurveyComplete />} />
          <Route path="/custom-test" element={<CustomTest />} />
          <Route path="/analysis-report" element={<AnalysisReport />} />
        </Routes>
      </main>
      
      <footer className="app-footer">
        <div className="footer-content">
          <nav className="footer-links" aria-label="Footer navigation">
            <Link to="/" className="footer-link">Home</Link>
            <Link to="/advanced-scenarios" className="footer-link">More Scenarios</Link>
            <Link to="/personal-questionnaire" className="footer-link">Assessment</Link>
            {currentUser ? (
              <Link to="/login" className="footer-link">Account</Link>
            ) : (
              <Link to="/register" className="footer-link">Register</Link>
            )}
            <a href="mailto:carl-1101@outlook.com" className="footer-link">Contact Us</a>
          </nav>
          <p className="copyright">&copy; {new Date().getFullYear()} Social Aptitude. All rights reserved.</p>
        </div>
      </footer>
      
      {/* 全局分享按钮 */}
      <ShareButton />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App; 